/**
 * Billing address form component
 */

import React from 'react';
import {
  TextField,
  Grid,
  MenuItem,
  FormControl,
  Select,
  FormHelperText,
  Box,
} from '@mui/material';
import { BillingAddress } from '../types/CardData';
import { COUNTRIES } from '../data/regionData';
import { POSTAL_CODE_MAX_LENGTH } from '../services/validationService';

export interface BillingAddressFormProps {
  address: Partial<BillingAddress>;
  onChange: (address: Partial<BillingAddress>) => void;
  errors?: Record<string, string>;
  disabled?: boolean;
}

const US_COUNTRY = COUNTRIES.find((c) => c.code === 'US')!;
const US_STATES = US_COUNTRY.states;

export const BillingAddressForm: React.FC<BillingAddressFormProps> = ({
  address,
  onChange,
  errors = {},
  disabled = false,
}) => {
  const handleFieldChange = (field: keyof BillingAddress) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    onChange({
      ...address,
      [field]: event.target.value,
    });
  };

  return (
    <Box>
      <Grid container rowSpacing={0} columnSpacing={2}>
        <Grid item xs={12} md={6}>
          <div className="field-container align-items-center">
            <div className="label">
              <label className="p-2" data-testid="address1" htmlFor="address1">
                Address Line 1
              </label>
              <span className="text-danger" data-testid="address1-Astrict-icon">
                *
              </span>
            </div>
            <TextField
              id="address1"
              className="textfield"
              fullWidth
              // label="Street Address"
              value={address.address1 || ''}
              onChange={handleFieldChange('address1')}
              error={!!errors.address1}
              helperText={errors.address1 || ' '}
              disabled={disabled}
              autoComplete="street-address"
              placeholder="123 Main St"
              required
              inputProps={{
                'aria-label': 'Street address',
                'aria-required': 'true',
              }}
            />
          </div>
        </Grid>

        <Grid item xs={12} md={6}>
          <div className="field-container align-items-center">
            <div className="label">
              <label className="p-2" data-testid="address2" htmlFor="address2">
                Address Line 2
              </label>
            </div>
            <TextField
              id="address2"
              className="textfield"
              fullWidth
              // label="Apartment, Suite, etc. (Optional)"
              value={address.address2 || ''}
              onChange={handleFieldChange('address2')}
              helperText={errors.address2 || ' '}
              disabled={disabled}
              error={!!errors.address2}
              autoComplete="address-line2"
              placeholder="Apt 4B"
              inputProps={{
                'aria-label': 'Apartment or suite number',
              }}
            />
          </div>
        </Grid>

        <Grid item xs={12} md={6}>
          <div className="field-container align-items-center">
            <div className="label">
              <label className="p-2" data-testid="City" htmlFor="city">
                City
              </label>
              <span className="text-danger" data-testid="City-Astrict-icon">
                *
              </span>
            </div>
            <TextField
              id="city"
              className="textfield"
              fullWidth
              // label="City"
              value={address.city || ''}
              onChange={handleFieldChange('city')}
              error={!!errors.city}
              helperText={errors.city || ' '}
              disabled={disabled}
              autoComplete="address-level2"
              placeholder="New York"
              required
              inputProps={{
                'aria-label': 'City',
                'aria-required': 'true',
              }}
            />
          </div>
        </Grid>

        <Grid item xs={12} md={6}>
          <div className="field-container align-items-center">
            <div className="label">
              <label className="p-2 pt-1" data-testid="State" id="state-label">
                State
              </label>
              <span className="text-danger" data-testid="State-Astrict-icon">
                *
              </span>
            </div>
            <FormControl
              fullWidth
              size="small"
              error={!!errors.state}
              required
              className="select-field"
              disabled={disabled}
            >
              <Select
                id="state"
                labelId="state-label"
                value={address.state || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  handleFieldChange("state")({
                    target: { value } as HTMLInputElement
                  } as React.ChangeEvent<HTMLInputElement>);
                }}
                MenuProps={{
                  autoFocus: false,
                  disableAutoFocusItem: true,
                  disableEnforceFocus: true,
                  disableScrollLock: true,
                  disableAutoFocus: true,
                  PaperProps: { sx: { ml: '12px' } },
                }}
                renderValue={(selected) => {
                  if (!selected) {
                    return <span className="selectPlaceholder">Select State</span>;
                  }
                  const option = US_STATES.find((s) => s.code === selected);
                  return option ? option.name : "";
                }}
                displayEmpty
                inputProps={{
                  "aria-label": "State",
                  "aria-required": "true",
                }}
              >
                {US_STATES.map((state) => (
                  <MenuItem key={state.code} value={state.code}>
                    {state.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{errors.state || " "}</FormHelperText>
            </FormControl>
          </div>
        </Grid>

        <Grid item xs={12} md={6}>
          <div className="field-container align-items-center">
            <div className="label">
              <label className="p-2" data-testid="ZIPCode" htmlFor="zipCode">
                ZIP Code
              </label>
              <span className="text-danger" data-testid="ZIPCode-Astrict-icon">
                *
              </span>
            </div>
            <TextField
              id="zipCode"
              className="textfield"
              fullWidth
              // label="ZIP Code"
              value={address.postalCode || ''}
              onChange={handleFieldChange('postalCode')}
              error={!!errors.postalCode}
              helperText={errors.postalCode || ' '}
              disabled={disabled}
              autoComplete="postal-code"
              inputMode="numeric"
              placeholder="12345"
              required
              inputProps={{
                'aria-label': 'ZIP code',
                'aria-required': 'true',
                maxLength: POSTAL_CODE_MAX_LENGTH,
              }}
            />
          </div>
        </Grid>

        <Grid item xs={12} md={6}>
          <div className="field-container align-items-center">
            <div className="label">
              <label className="p-2 pt-1" data-testid="Country" id="country-label">
                Country
              </label>
              <span className="text-danger" data-testid="Country-Astrict-icon">
                *
              </span>
            </div>
            <FormControl
              fullWidth
              size="small"
              error={!!errors.country}
              required
              className="select-field"
              disabled={true}
            >
              <Select
                id="country"
                labelId="country-label"
                value="US"
                disabled
                displayEmpty
                inputProps={{
                  "aria-label": "Country",
                  "aria-required": "true",
                }}
              >
                <MenuItem value="US">{US_COUNTRY.name}</MenuItem>
              </Select>
              <FormHelperText>{errors.country || " "}</FormHelperText>
            </FormControl>
          </div>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BillingAddressForm;
